/**
 * GeometryUtils - Simple geometric calculation utilities
 * This is a small, focused module with no external dependencies
 */
import { PolygonUtil } from './polygon.util';

export class GeometryUtils {
  /**
   * Calculate angle between three points
   */
  static calculateAngle(p1: L.LatLngLiteral, p2: L.LatLngLiteral, p3: L.LatLngLiteral): number {
    const v1 = { x: p1.lng - p2.lng, y: p1.lat - p2.lat };
    const v2 = { x: p3.lng - p2.lng, y: p3.lat - p2.lat };

    const dot = v1.x * v2.x + v1.y * v2.y;
    const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

    if (mag1 === 0 || mag2 === 0) return 0;

    const cosAngle = dot / (mag1 * mag2);
    return Math.acos(Math.max(-1, Math.min(1, cosAngle)));
  }

  /**
   * Calculate distance from point to line between two other points
   */
  static calculateDistanceFromLine(
    p1: L.LatLngLiteral,
    point: L.LatLngLiteral,
    p2: L.LatLngLiteral,
  ): number {
    const A = point.lng - p1.lng;
    const B = point.lat - p1.lat;
    const C = p2.lng - p1.lng;
    const D = p2.lat - p1.lat;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) return Math.sqrt(A * A + B * B);

    const param = dot / lenSq;

    let xx, yy;
    if (param < 0) {
      xx = p1.lng;
      yy = p1.lat;
    } else if (param > 1) {
      xx = p2.lng;
      yy = p2.lat;
    } else {
      xx = p1.lng + param * C;
      yy = p1.lat + param * D;
    }

    const dx = point.lng - xx;
    const dy = point.lat - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Calculate centroid of polygon
   * @deprecated Use PolygonUtil.getCenter() instead for consistency
   */
  static calculateCentroid(latlngs: L.LatLngLiteral[]): L.LatLngLiteral {
    // This method is deprecated - use PolygonUtil.getCenter() for consistency
    // Keeping this as a thin wrapper to avoid breaking changes
    return PolygonUtil.getCenter(latlngs);
  }

  /**
   * Calculate distance between two points
   */
  static calculateDistance(p1: L.LatLngLiteral, p2: L.LatLngLiteral): number {
    const dx = p1.lng - p2.lng;
    const dy = p1.lat - p2.lat;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Apply offset to polygon coordinates
   * Note: Uses 'any' type for recursive coordinate structures that can be:
   * - L.LatLngLiteral (single coordinate)
   * - L.LatLngLiteral[] (array of coordinates)
   * - L.LatLngLiteral[][] (polygon with holes)
   * - L.LatLngLiteral[][][] (multipolygon)
   * This is a legitimate use of 'any' for dynamic recursive data structures.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static offsetPolygonCoordinates(latLngs: any, offsetLat: number, offsetLng: number): any {
    if (!latLngs) return latLngs;

    if (Array.isArray(latLngs[0])) {
      // Multi-dimensional array (polygon with holes or multipolygon)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return latLngs.map((ring: any) =>
        GeometryUtils.offsetPolygonCoordinates(ring, offsetLat, offsetLng),
      );
    } else if (latLngs.lat !== undefined && latLngs.lng !== undefined) {
      // Single coordinate
      return {
        lat: latLngs.lat + offsetLat,
        lng: latLngs.lng + offsetLng,
      };
    } else {
      // Array of coordinates
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return latLngs.map((coord: any) =>
        GeometryUtils.offsetPolygonCoordinates(coord, offsetLat, offsetLng),
      );
    }
  }
}
